import React, {useContext, useEffect, useState} from "react";
import {
  Autocomplete, Avatar, Box, Button, CircularProgress, Fade, IconButton, InputBase, MenuItem, Modal, Select, Stack,
  Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Cancel01Icon, Delete02Icon, UserGroupIcon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {ProjectRoles} from "../../../context/AppContext";
import useConfirm from "../../../hooks/useConfirm";
import {ProjectRepository} from "../../../repositories/ProjectRepository";
import {useRegistryProject} from "../RegistryProjectContext";
import {controlSx, labelSx, selectMenuProps, SectionHeader} from "./settingsStyles";

const ROLES = Object.values(ProjectRoles);

const getErrorMessage = (e, fallback) => e?.response?.data?.message || fallback;

const RoleSelect = ({sx, ...props}) => (
  <Select
    variant={"standard"}
    disableUnderline
    sx={{...controlSx, textTransform: "capitalize", "& .MuiSelect-select": {py: 0, pr: "22px !important"}, "& .MuiSelect-icon": {color: "#6B6B80"}, ...sx}}
    MenuProps={selectMenuProps}
    {...props}
  >
    {ROLES.map(role => (
      <MenuItem key={role} value={role} sx={{textTransform: "capitalize", fontSize: 13}}>{role.toLowerCase()}</MenuItem>
    ))}
  </Select>
);

const primaryButtonSx = {
  textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px",
  bgcolor: "rgba(132, 145, 247, 0.12)", color: "#4B55C9", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.2)"},
  "&.Mui-disabled": {bgcolor: "#F0F0F6", color: "#B4B4C3"},
};

const ghostButtonSx = {
  textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px",
  color: "#374151", bgcolor: "transparent", "&:hover": {bgcolor: "#ECECF3"},
};

// Members tab: who can access the project, with which role and VDN. Changes are saved immediately.
export const RegistryMembersSettings = () => {
  const {project, members, refreshProject} = useRegistryProject();
  const {currentUser, organizations} = useContext(SignInContext);
  const [addMember, setAddMember] = useState(false);
  // id of the member being saved
  const [saving, setSaving] = useState(null);

  const confirmRemove = useConfirm("Are you sure you want to remove this member?");

  const organization = organizations?.find(item => item.id === project.organizationId);
  const candidates = (organization?.members || [])
    .map(item => item?.member)
    .filter(username => username && !members.some(member => member.username === username));

  // saves through the Project API only (members are no longer mirrored to the Firebase flows)
  const save = async (key, request, successMessage, errorMessage) => {
    setSaving(key);
    try {
      await request();
      await refreshProject();
      toast.success(successMessage);
      return true;
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, errorMessage));
      return false;
    } finally {
      setSaving(null);
    }
  };

  const handleAdd = ({username, role, vdn}) => save(
    "new",
    () => ProjectRepository.addMember(project.id, {username, role, vdn}),
    `Added "${username}" to the project`,
    "Failed to add member"
  );

  // changes: {role} or {vdn}; the API takes both on every edit
  const handleEdit = (member, changes) => {
    const {role, vdn} = {role: member.role, vdn: member.vdn || "", ...changes};
    const {username} = member;
    return save(
      member.id,
      () => ProjectRepository.editMember(project.id, member.id, {role, vdn}),
      `Updated "${username}"`,
      "Failed to update member"
    );
  };

  const handleRemove = async (member) => {
    const confirmed = await confirmRemove({title: `Remove "${member.username}" from the project?`});
    if (!confirmed) return;
    await save(
      member.id,
      () => ProjectRepository.removeMember(project.id, member.id),
      `Removed "${member.username}" from the project`,
      "Failed to remove member"
    );
  };

  return (
    <>
      <AddMemberDialog
        open={addMember}
        handleClose={() => setAddMember(false)}
        candidates={candidates}
        members={members}
        onSubmit={handleAdd}
      />
      <Box sx={{px: {xs: 2, md: 4}, py: {xs: 2, md: 3}}}>
        <SectionHeader
          icon={<HugeiconsIcon icon={UserGroupIcon} size={18}/>}
          title={"Members"}
          description={"Project access is granted to members, each with a specific role."}
          action={
            <Button
              onClick={() => setAddMember(true)}
              startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
              sx={{...primaryButtonSx, width: {xs: "100%", sm: "auto"}}}
            >
              Add Member
            </Button>
          }
        />

        <Stack direction={"column"} gap={1.25}>
          {members.length === 0 && (
            <Typography sx={{fontSize: 13, color: "#8A8AA0", py: 2, textAlign: "center"}}>
              No members yet. Add your first team member.
            </Typography>
          )}
          {members.map(member => (
            <MemberRow
              key={member.id}
              member={member}
              isSelf={member.username === currentUser?.username}
              saving={saving === member.id}
              onEdit={handleEdit}
              onRemove={handleRemove}
            />
          ))}
        </Stack>
      </Box>
    </>
  );
};

const MemberRow = ({member, isSelf, saving, onEdit, onRemove}) => {
  const [vdn, setVdn] = useState(member.vdn || "");
  const fullName = [member.name, member.lastname].filter(Boolean).join(" ");

  useEffect(() => {
    setVdn(member.vdn || "");
  }, [member.vdn]);

  // the VDN is saved when the field loses focus
  const saveVdn = async () => {
    const value = vdn.trim();
    if (value === (member.vdn || "")) return;
    const success = await onEdit(member, {vdn: value});
    if (!success) setVdn(member.vdn || "");
  };

  return (
    <Stack
      direction={{xs: "column", sm: "row"}}
      alignItems={{xs: "stretch", sm: "center"}}
      gap={1.5}
      sx={{p: 1.5, borderRadius: "12px", bgcolor: "#FAFAFD"}}
    >
      <Stack direction={"row"} alignItems={"center"} gap={1.5} sx={{flex: 1, minWidth: 0}}>
        <Avatar sx={{
          width: 36, height: 36, fontSize: "0.85rem", fontWeight: 600,
          bgcolor: "rgba(132, 145, 247, 0.14)", color: "#4B55C9"
        }}>
          {(fullName || member.username)?.[0]?.toUpperCase() || "?"}
        </Avatar>
        <Box sx={{minWidth: 0}}>
          <Typography sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}} noWrap title={fullName || member.username}>
            {fullName || member.username}
          </Typography>
          {fullName && (
            <Typography sx={{fontSize: 11, color: "#8A8AA0", display: "block"}} noWrap>
              {member.username}
            </Typography>
          )}
        </Box>
      </Stack>
      <Box sx={{...controlSx, minWidth: {xs: "100%", sm: 170}, maxWidth: {sm: 220}}}>
        <InputBase
          placeholder={"VDN (Transfer Number)"}
          value={vdn}
          disabled={saving}
          fullWidth
          sx={{flex: 1, fontSize: 13, color: "#23264D"}}
          onChange={e => setVdn(e.target.value)}
          onBlur={saveVdn}
          onKeyDown={e => e.key === "Enter" && e.target.blur()}
        />
      </Box>
      <Tooltip title={isSelf ? "You cannot change your own role" : ""}>
        <span>
          <RoleSelect
            value={member.role || ""}
            disabled={isSelf || saving}
            sx={{minWidth: {xs: "100%", sm: 140}}}
            onChange={e => onEdit(member, {role: e.target.value})}
          />
        </span>
      </Tooltip>
      <Tooltip title={isSelf ? "You cannot remove yourself" : "Remove Member"}>
        <Box component={"span"} sx={{alignSelf: {xs: "flex-end", sm: "center"}}}>
          <IconButton size={"small"} disabled={isSelf || saving} onClick={() => onRemove(member)} sx={{color: "#DC2626"}}>
            <HugeiconsIcon icon={Delete02Icon} size={18}/>
          </IconButton>
        </Box>
      </Tooltip>
    </Stack>
  );
};

const AddMemberDialog = ({open, handleClose, candidates, members, onSubmit}) => {
  const [username, setUsername] = useState("");
  const [role, setRole] = useState(ProjectRoles.AGENT);
  const [vdn, setVdn] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setUsername("");
    setRole(ProjectRoles.AGENT);
    setVdn("");
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = username.trim();
    if (!name) return;
    if (members.some(member => member.username === name)) {
      toast.error("User is already a member of this project");
      return;
    }
    setLoading(true);
    const success = await onSubmit({username: name, role, vdn: vdn.trim()});
    setLoading(false);
    if (success) handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Fade in={open}>
        <Box
          component={"form"}
          onSubmit={handleSubmit}
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: {xs: "calc(100% - 24px)", md: "min(440px, 92vw)"}, bgcolor: "#fff", borderRadius: "20px",
            boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)", p: {xs: 2.5, md: 3}, outline: "none",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2.5}}>
            <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>Add Project Member</Typography>
            <IconButton size="small" onClick={handleClose} disabled={loading} sx={{color: "#8A8AA0"}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Stack gap={2} sx={{mb: 2.5}}>
            <Box>
              <Typography sx={labelSx}>Username</Typography>
              <Autocomplete
                freeSolo
                options={candidates}
                inputValue={username}
                onInputChange={(e, value) => setUsername(value)}
                renderInput={(params) => (
                  <Box ref={params.InputProps.ref} sx={controlSx}>
                    <InputBase {...params.inputProps} fullWidth sx={{flex: 1, fontSize: 13, color: "#23264D"}}/>
                  </Box>
                )}
              />
            </Box>
            <Box>
              <Typography sx={labelSx}>Role</Typography>
              <RoleSelect fullWidth value={role} onChange={e => setRole(e.target.value)}/>
            </Box>
            <Box>
              <Typography sx={labelSx}>VDN Number</Typography>
              <Box sx={controlSx}>
                <InputBase
                  value={vdn}
                  onChange={e => setVdn(e.target.value)}
                  fullWidth
                  inputProps={{"aria-label": "VDN number"}}
                  sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
              <Typography sx={{fontSize: 11, color: "#8A8AA0", mt: 0.75}}>Optional transfer number</Typography>
            </Box>
          </Stack>

          <Stack direction="row" gap={1.5} justifyContent="flex-end">
            <Button type={"button"} onClick={handleClose} disabled={loading} sx={ghostButtonSx}>Cancel</Button>
            <Button
              type={"submit"} disabled={loading || !username.trim()}
              startIcon={loading ? <CircularProgress size={14} sx={{color: "#4B55C9"}}/> : null}
              sx={primaryButtonSx}
            >
              Add Member
            </Button>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};
